import React, { useState, useEffect } from 'react';
import { 
  Bike, Zap, Battery, MapPin, Clock, Shield, Phone, ChevronDown,
  Leaf, TrendingUp, CheckCircle, ArrowRight, Package, Loader2
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API = process.env.REACT_APP_BACKEND_URL;

const MovilidadElectricaPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [motos, setMotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchMotos();
  }, []);

  const fetchMotos = async () => {
    try {
      const response = await fetch(`${API}/api/products/public/catalog?category=moto_electrica`);
      if (response.ok) {
        const data = await response.json();
        setMotos(data);
      }
    } catch (error) {
      console.error('Error fetching motos:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const benefits = [
    {
      icon: Leaf,
      title: 'Cero Emisiones',
      description: 'Contribuí al medio ambiente sin contaminar el aire de tu ciudad.'
    },
    {
      icon: TrendingUp,
      title: 'Ahorro en Combustible',
      description: 'El costo por km es hasta 90% menor que una moto a nafta.'
    },
    {
      icon: Shield,
      title: 'Bajo Mantenimiento',
      description: 'Sin cambios de aceite, filtros ni ajustes de motor. Solo frenos y neumáticos.'
    },
    {
      icon: Zap,
      title: 'Carga en Casa',
      description: 'Enchufá tu moto en cualquier tomacorriente estándar.'
    }
  ];

  const chargingServices = [
    {
      title: 'Estaciones de Carga Residenciales',
      description: 'Instalamos puntos de carga en tu hogar para cargar tu moto o auto eléctrico durante la noche.',
      features: ['Instalación profesional', 'Compatible con todos los vehículos', 'Monitoreo de consumo']
    },
    {
      title: 'Estaciones de Carga Comerciales',
      description: 'Convertí tu negocio en un punto de carga y atraé clientes mientras esperan.',
      features: ['Carga rápida DC', 'Sistema de cobro integrado', 'Señalización incluida']
    },
    {
      title: 'Estaciones de Carga Solar',
      description: 'La solución más sustentable: cargá tu vehículo con energía 100% solar.',
      features: ['Paneles solares incluidos', 'Independencia de la red', 'Cero costo operativo']
    }
  ];

  const faqs = [
    {
      question: '¿Cuántos km de autonomía tienen las motos eléctricas?',
      answer: 'Dependiendo del modelo, nuestras motos tienen entre 60 y 150 km de autonomía por carga. Los modelos con batería extraíble permiten llevar una batería de repuesto para duplicar la autonomía.'
    },
    {
      question: '¿Cuánto tiempo tarda en cargar?',
      answer: 'Una carga completa toma entre 4 y 8 horas en un tomacorriente estándar. Con cargador rápido, podés alcanzar el 80% en 1-2 horas.'
    },
    {
      question: '¿Qué garantía tienen las motos eléctricas?',
      answer: 'Ofrecemos garantía de 2 años en el motor eléctrico y controlador, y 1 año en la batería de litio. El servicio técnico es local en Paraguay.'
    },
    {
      question: '¿Necesito licencia especial para manejar moto eléctrica?',
      answer: 'No, las motos eléctricas de baja cilindrada equivalente se manejan con la misma licencia que una moto convencional.'
    },
    {
      question: '¿Qué pasa si llueve o lavo la moto?',
      answer: 'Las motos eléctricas están diseñadas para uso exterior normal. Tienen protección IP65 contra agua y polvo. Solo evitá sumergirla o usar hidrolavadora directamente en los componentes eléctricos.'
    }
  ];

  const handleWhatsAppContact = (product) => {
    const message = product 
      ? `Hola Zenex! Me interesa la moto eléctrica: ${product.name}. ¿Me pueden dar más información?`
      : `Hola Zenex! Me interesa conocer sobre las motos eléctricas y estaciones de carga.`;
    window.open(`https://wa.me/595991344956?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden" data-testid="movilidad-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#90EE90] rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <Bike className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Movilidad Eléctrica</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              El Futuro de la Movilidad es
              <span className="text-[#90EE90]"> Eléctrico</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Motos eléctricas de última generación y estaciones de carga para hogares y negocios. 
              Movilidad sustentable para Paraguay.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => handleWhatsAppContact()}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                <Phone size={20} />
                Consultar Ahora
              </button>
              <a
                href="#motos"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                Ver Motos
                <ArrowRight size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-zinc-50" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              ¿Por qué eléctrico?
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Ventajas de la Movilidad Eléctrica
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div 
                  key={index}
                  className="bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-zinc-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Motos Section */}
      <section id="motos" className="py-16 md:py-24" data-testid="motos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Catálogo
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Motos Eléctricas en Stock
            </h2>
            <p className="text-zinc-600 max-w-2xl mx-auto">
              Modelos seleccionados con garantía y servicio técnico local
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#90EE90] animate-spin" />
              <span className="ml-3 text-zinc-500">Cargando productos...</span>
            </div>
          ) : motos.length === 0 ? (
            <div className="text-center py-16 bg-zinc-50 rounded-2xl">
              <Bike className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-zinc-900 mb-2">Próximamente</h3>
              <p className="text-zinc-500 mb-6">
                Estamos preparando nuestro stock de motos eléctricas. ¡Consultanos para ser el primero en enterarte!
              </p>
              <button
                onClick={() => handleWhatsAppContact()}
                className="px-6 py-3 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors"
              >
                Avisarme cuando lleguen
              </button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {motos.map((moto) => (
                <div 
                  key={moto.id}
                  className="bg-white rounded-2xl border border-zinc-200 overflow-hidden hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="relative h-56 bg-zinc-100">
                    {moto.image_url ? (
                      <img
                        src={moto.image_url}
                        alt={moto.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Bike className="w-20 h-20 text-zinc-300" />
                      </div>
                    )}
                    {moto.is_featured && (
                      <div className="absolute top-4 left-4 bg-[#90EE90] text-zinc-900 px-3 py-1 rounded-full text-xs font-bold">
                        Destacado
                      </div>
                    )}
                  </div>
                  
                  <div className="p-6">
                    <p className="text-xs text-zinc-500 uppercase mb-1">{moto.brand}</p>
                    <h3 className="text-xl font-bold text-zinc-900 mb-2">{moto.name}</h3>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {moto.power_watts && (
                        <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-1 rounded">
                          {moto.power_watts}W
                        </span>
                      )}
                      {moto.battery_capacity_kwh && (
                        <span className="text-xs bg-zinc-100 text-zinc-600 px-2 py-1 rounded">
                          {moto.battery_capacity_kwh} kWh
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-zinc-600 mb-4 line-clamp-2">{moto.short_description || moto.description}</p>
                    
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-bold text-zinc-900">{formatPrice(moto.price_usd)}</p>
                        <p className="text-xs text-zinc-500">{moto.warranty_years} años garantía</p>
                      </div>
                      <button
                        onClick={() => handleWhatsAppContact(moto)}
                        className="px-4 py-2 bg-[#90EE90] text-zinc-900 font-bold text-sm rounded-lg hover:bg-[#7CDF7C] transition-colors"
                      >
                        Consultar
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Charging Stations Section */}
      <section className="py-16 md:py-24 bg-zinc-900" data-testid="charging-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Infraestructura
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Estaciones de Carga
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Instalamos puntos de carga para vehículos eléctricos en hogares, empresas y espacios públicos
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {chargingServices.map((service, index) => (
              <div 
                key={index}
                className="bg-zinc-800 p-6 rounded-2xl border border-zinc-700 hover:border-[#90EE90] transition-all duration-300"
              >
                <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                  <Zap className="w-6 h-6 text-[#90EE90]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{service.title}</h3>
                <p className="text-zinc-400 text-sm mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
                      <CheckCircle className="w-4 h-4 text-[#90EE90]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => handleWhatsAppContact()}
              className="px-8 py-4 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors inline-flex items-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Solicitar Cotización de Estación
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre Movilidad Eléctrica
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div 
                key={index}
                className="border border-zinc-200 rounded-xl overflow-hidden bg-white"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#14532D] to-[#166534]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Bike className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Unite a la Revolución Eléctrica
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Consultanos sobre motos eléctricas, estaciones de carga y financiamiento disponible.
          </p>
          <button
            onClick={() => handleWhatsAppContact()}
            className="px-8 py-4 bg-[#90EE90] text-zinc-900 font-bold rounded-xl hover:bg-[#7CDF7C] transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Contactar Ahora
          </button>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default MovilidadElectricaPage;
